import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  FileText,
  Calculator,
  Users,
  CheckSquare,
  Calendar,
  Archive,
  LogOut,
  Search,
  Bell,
  ChevronDown,
  ChevronRight,
  Plus,
  Clock,
  ArrowLeftRight,
  Settings,
  Building2,
  GraduationCap,
  Headphones,
  MessageSquare,
  Lightbulb,
  Info,
  Shield,
  Accessibility,
  Cog
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = ({ children, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Auto open/close sidebar on resize
  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateNewCase = async () => {
    if (isCreatingCase) return;
    setIsCreatingCase(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cases/quick`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const newCase = await response.json();
        navigate(`/neuer-fall?edit=${newCase.id}`);
      }
    } catch (error) {
      console.error('Error creating case:', error);
    } finally {
      setIsCreatingCase(false);
    }
  };

  const menuItems = [
    { icon: FileText, label: 'Akten', href: '/dashboard' },
    { icon: Calculator, label: 'Buchhaltung', href: '/buchhaltung' },
    { icon: Users, label: 'Kunden', href: '/kunden' },
    { icon: CheckSquare, label: 'Aufgaben', href: '/aufgaben' },
    { icon: Calendar, label: 'Termine', href: '/termine' },
    { icon: Archive, label: 'Archiv', href: '/archiv' },
  ];

  const newCaseOptions = [
    {
      icon: ArrowLeftRight,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      title: 'Austausch',
      subtitle: 'Scheibenaustausch',
      href: '/neuer-fall/austausch'
    },
    {
      icon: Cog,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      title: 'Reparatur',
      subtitle: 'Reparaturauftrag anlegen',
      href: '/neuer-fall/reparatur'
    },
    {
      icon: FileText,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: 'Kostenvoranschlag',
      subtitle: 'Kostenvoranschlag erstellen',
      href: '/neuer-fall/kostenvoranschlag'
    }
  ];

  const userMenuItems = [
    { icon: Building2, label: 'Unternehmen', href: '/unternehmen' },
    { icon: Settings, label: 'Einstellungen', href: '/einstellungen' },
    { icon: GraduationCap, label: 'Schulung', href: '/schulung' },
    { icon: Headphones, label: 'Support', href: '/support' },
    { icon: MessageSquare, label: 'Chat', href: '/chat' },
    { icon: Lightbulb, label: 'Verbesserung', href: '/verbesserung' },
  ];

  const legalItems = [
    { icon: Info, label: 'Impressum', href: '/impressum' },
    { icon: Shield, label: 'Datenschutz', href: '/datenschutz' },
    { icon: Accessibility, label: 'Barrierefreiheit', href: '/barrierefreiheit' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const currentTime = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const currentDate = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
  const currentWeek = `KW${Math.ceil((new Date().getDate() + new Date(new Date().getFullYear(), new Date().getMonth(), 1).getDay()) / 7)}`;

    return (
    <div className="min-h-screen bg-white lg:bg-gray-50 flex flex-col">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 h-14 lg:h-16 flex items-center px-3 lg:px-4 sticky top-0 z-50">
        <div className="flex items-center gap-2 lg:gap-4 flex-1">
          {/* Menu Toggle */}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          
          {/* Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_agency-dashboard-38/artifacts/ve9u3f19_logo_sharp_4x%20%281%29.png" 
            alt="Autogutachter24" 
            className="h-6 lg:h-7 object-contain hidden sm:block"
          />
          
          {/* Dashboard Title */}
          <span className="text-gray-700 font-medium hidden lg:block">Dashboard</span>
          
          {/* New Case Button */}
          <Button 
            onClick={handleCreateNewCase}
            disabled={isCreatingCase}
            className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-1 lg:gap-2 ml-1 lg:ml-2 h-8 lg:h-9 px-2 lg:px-4 shadow-sm"
            data-testid="new-case-btn"
          >
            <Plus className="w-4 h-4" />
            <span>{isCreatingCase ? 'Wird erstellt...' : 'Neuer Fall'}</span>
          </Button>
        </div>
        
        {/* Right Side */}
        <div className="flex items-center gap-2 lg:gap-3">
          {/* Time Badge */}
                    <div className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            {currentTime}
          </div>
          
          {/* Date Badge */}
          <div className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            {currentDate} • {currentWeek}
          </div>
          
          {/* Active Badge */}
          <div className="hidden 2xl:flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-600">
            <CheckSquare className="w-4 h-4" />
            0 Aktiv
          </div>
          
          {/* Search */}
          <div className="hidden xl:flex items-center relative">
            <Search className="w-4 h-4 absolute left-3 text-gray-400" />
            <Input 
              placeholder="Suchen..." 
              className="pl-9 w-32 lg:w-40 h-8 lg:h-9 bg-gray-50 border-gray-200 rounded-lg text-sm"
            />
          </div>
          
          {/* Notification */}
          <button className="p-1.5 lg:p-2 hover:bg-gray-100 rounded-lg transition-colors relative">
            <Bell className="w-5 h-5 text-gray-600" />
          </button>
          
          {/* User Profile Dropdown */}
          <div className="relative" ref={userMenuRef}>
            <button 
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 lg:gap-3 pl-2 lg:pl-3 border-l border-gray-200 hover:bg-gray-50 rounded-lg px-1 lg:px-2 py-1 transition-colors"
            >
              <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-gradient-to-r from-[#29beff] to-[#5dd3ff] flex items-center justify-center text-white font-medium text-xs lg:text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'S'}
              </div>
              <div className="hidden lg:block text-right">
                <p className="text-sm font-medium text-gray-900">Autogutachter24 GmbH</p>
                <p className="text-xs text-gray-500">admin</p>
              </div>
            </button>
            
            {/* User Dropdown Menu */}
            {userMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-64 lg:w-72 bg-white rounded-xl shadow-xl border border-gray-200 py-3 z-50">
                {/* User Info */}
                <div className="px-4 pb-3 border-b border-gray-100">
                  <p className="font-semibold text-gray-900">{user?.name || 'Super Admin'}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                                        <div className="w-5 h-5 rounded bg-gradient-to-r from-[#29beff] to-[#5dd3ff] flex items-center justify-center">
                      <Building2 className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm font-medium text-blue-600">Autogutachter24 GmbH</span>
                  </div>
                </div>
                
                {/* Menu Items */}
                <div className="py-2">
                  {userMenuItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.href}
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      <item.icon className="w-5 h-5 text-gray-400" />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
                
                {/* Legal Items */}
                <div className="py-2 border-t border-gray-100">
                  {legalItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.href}
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      <item.icon className="w-5 h-5 text-gray-400" />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
                
                {/* Logout */}
                <div className="pt-2 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 transition-colors text-red-500 w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Abmelden</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      
            <div className="flex flex-1">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:relative z-50 lg:z-auto top-0 left-0 h-full lg:h-auto
          bg-white border-r border-gray-200 transition-all duration-300 flex flex-col
          ${sidebarOpen ? 'w-56 translate-x-0' : 'w-56 -translate-x-full lg:w-16 lg:translate-x-0'}
          lg:flex-shrink-0
        `}>
          {/* Logo and Close */}
          <div className={`${sidebarOpen ? 'p-4' : 'p-2'} border-b border-gray-100 flex items-center justify-between`}>
            {sidebarOpen ? (
              <img 
                src="https://customer-assets.emergentagent.com/job_agency-dashboard-38/artifacts/ve9u3f19_logo_sharp_4x%20%281%29.png" 
                alt="Autogutachter24" 
                className="h-8 object-contain"
              />
            ) : (
              <div className="w-full flex justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_agency-dashboard-38/artifacts/ve9u3f19_logo_sharp_4x%20%281%29.png" 
                  alt="Autogutachter24" 
                  className="h-6 object-contain"
                />
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400 lg:hidden"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-8 h-8 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hidden lg:flex"
            >
              <ChevronRight className={`w-5 h-5 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
          
          {/* Menu Items */}
          <nav className="flex-1 p-2">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.href || (item.href === '/dashboard' && location.pathname === '/dashboard');
                return (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      title={item.label}
                      onClick={() => { if (window.innerWidth < 1024) setSidebarOpen(false); }}
                      className={`flex items-center ${sidebarOpen ? 'gap-3 px-4' : 'justify-center px-2'} py-3 rounded-lg transition-all text-[14px] leading-[20px] ${
                        isActive 
                          ? 'bg-gradient-to-r from-[#29beff] to-[#5dd3ff] text-white' 
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {sidebarOpen && <span className="font-medium text-[14px] leading-[20px]">{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
          
          {/* Logout */}
          <div className="p-2 border-t border-gray-100">
            <button
              onClick={handleLogout}
              title="Abmelden"
              className={`flex items-center ${sidebarOpen ? 'gap-3 px-4' : 'justify-center px-2'} py-3 rounded-lg text-red-500 hover:bg-red-50 w-full transition-colors`}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium text-[14px] leading-[20px]">Abmelden</span>}
            </button>
          </div>
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
